import { gql } from '@apollo/client';

const all = gql`
  fragment AnesthesiologistData on Anesthesiologist {
    id
    name
    email
    hstId
    phoneNumber
    hasScheduleAccess
    scheduleAccessAll
    shouldSendNotification
    hourlyRate
    isCrna
  }
`;

// Minimal fragment for schedule display (only fields actually used in schedule views)
const anesthesiologistDataSchedule = gql`
  fragment AnesthesiologistDataSchedule on Anesthesiologist {
    id
    name
  }
`;

export const fragments = {
  all: gql`
    fragment AnesthesiologistData on Anesthesiologist {
      id
      name
      email
      hstId
      phoneNumber
      hasScheduleAccess
      scheduleAccessAll
      shouldSendNotification
      hourlyRate
      isCrna
    }
  `,
  anesthesiologistDataSchedule,
  shift: gql`
    fragment AnesthesiologistShiftRoomData on AnesthesiologistShiftRoom {
      id
      anesthesiologist {
        ...AnesthesiologistData
      }
    }
    ${all}
  `,
  // Minimal shift fragment for schedule display
  shiftSchedule: gql`
    fragment AnesthesiologistShiftRoomDataSchedule on AnesthesiologistShiftRoom {
      id
      anesthesiologist {
        ...AnesthesiologistDataSchedule
      }
    }
    ${anesthesiologistDataSchedule}
  `,
};

export const list = gql`
  query anesthesiologists {
    anesthesiologists {
      ...AnesthesiologistData
      pin
      bracelet {
        id
        batteryLevel
        updatedAt
      }
      room {
        id
        name
      }
    }
  }
  ${fragments.all}
`;

export const item = gql`
  query anesthesiologist($id: Long!) {
    anesthesiologist(id: $id) {
      ...AnesthesiologistData
      bracelet {
        id
        batteryLevel
        updatedAt
      }
      room {
        id
        name
      }
    }
  }
  ${fragments.all}
`;

export const create = gql`
  mutation createAnesthesiologist(
    $name: String!
    $braceletId: String
    $hstId: HstId
    $phoneNumber: String
    $email: String
    $shouldSendNotification: Boolean
    $hourlyRate: Float
    $isCrna: Boolean
  ) {
    createAnesthesiologist(
      name: $name
      braceletId: $braceletId
      hstId: $hstId
      phoneNumber: $phoneNumber
      email: $email
      shouldSendNotification: $shouldSendNotification
      hourlyRate: $hourlyRate
      isCrna: $isCrna
    ) {
      ...AnesthesiologistData
    }
  }
  ${fragments.all}
`;

export const update = gql`
  mutation editAnesthesiologist(
    $id: Long!
    $braceletId: String
    $hstId: HstId
    $name: String!
    $phoneNumber: String
    $email: String
    $shouldSendNotification: Boolean
    $hourlyRate: Float
    $isCrna: Boolean
  ) {
    editAnesthesiologist(
      id: $id
      name: $name
      braceletId: $braceletId
      hstId: $hstId
      phoneNumber: $phoneNumber
      email: $email
      shouldSendNotification: $shouldSendNotification
      hourlyRate: $hourlyRate
      isCrna: $isCrna
    ) {
      ...AnesthesiologistData
    }
  }
  ${fragments.all}
`;

export const remove = gql`
  mutation deleteAnesthesiologist($id: Long!) {
    deleteAnesthesiologist(id: $id)
  }
`;

export const grantScheduleAccess = gql`
  mutation grantScheduleAccessToAnesthesiologist($id: Long!) {
    grantScheduleAccessToAnesthesiologist(id: $id) {
      ...AnesthesiologistData
    }
  }
  ${fragments.all}
`;

export const resendScheduleAccessLink = gql`
  mutation resendScheduleAccessLinkToAnesthesiologist($id: Long!) {
    resendScheduleAccessLinkToAnesthesiologist(id: $id) {
      ...AnesthesiologistData
    }
  }
  ${fragments.all}
`;

export const setScheduleAccessAllAnesthesiologist = gql`
  mutation setScheduleAccessAllAnesthesiologist($id: Long!, $scheduleAccessAll: Boolean!) {
    setScheduleAccessAllAnesthesiologist(id: $id, scheduleAccessAll: $scheduleAccessAll)
  }
`;

export const getScheduleAccessAllAnesthesiologist = gql`
  query getScheduleAccessAllAnesthesiologist($id: Long) {
    getScheduleAccessAllAnesthesiologist(id: $id)
  }
`;

export const revokeScheduleAccess = gql`
  mutation revokeScheduleAccessToAnesthesiologist($id: Long!) {
    revokeScheduleAccessToAnesthesiologist(id: $id) {
      ...AnesthesiologistData
    }
  }
  ${fragments.all}
`;

export const listAssignedAnesthesiologistsSubscription = gql`
  subscription listAssignedAnesthesiologists($date: Date!) {
    listAssignedAnesthesiologists(date: $date) {
      ...AnesthesiologistData
    }
  }
  ${fragments.all}
`;

export const sendNewScheduleNotification = gql`
  mutation sendNewScheduleNotificationAnesthesiologist($contacts: [ContactInput!]!, $message: String!, $date: Date!) {
    sendNewScheduleNotificationAnesthesiologist(contacts: $contacts, message: $message, date: $date)
  }
`;

/**
 * Paginated query for anesthesiologists.
 * Uses SQL LIMIT/OFFSET for efficient server-side pagination.
 */
export const anesthesiologistsPaginatedQuery = gql`
  query anesthesiologistsPaginated($offset: Int, $limit: Int, $sortBy: String, $sortOrder: String, $name: String) {
    anesthesiologistsPaginated(offset: $offset, limit: $limit, sortBy: $sortBy, sortOrder: $sortOrder, name: $name) {
      ...AnesthesiologistData
      pin
      bracelet {
        id
        batteryLevel
        updatedAt
      }
      room {
        id
        name
      }
    }
  }
  ${fragments.all}
`;

/**
 * Count query for anesthesiologists.
 * Returns total count for pagination controls.
 */
export const anesthesiologistsCountQuery = gql`
  query anesthesiologistsCount {
    anesthesiologistsCount
  }
`;

export default {
  list,
  item,
  create,
  update,
  remove,
  grantScheduleAccess,
  resendScheduleAccessLink,
  revokeScheduleAccess,
  setScheduleAccessAllAnesthesiologist,
  anesthesiologistsPaginatedQuery,
  anesthesiologistsCountQuery,
};
